import { observable, computed, action } from 'mobx';

import logger from 'logger';
import { translate } from 'services/i18nService';
import LocalStorageService from 'services/LocalStorageService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  HLS_DEBUG,
  HLS_DETACH,
  PRIVATE_SHOW_STOP_SPY,
  STREAM_STARTED,
  STREAM_STARTED_TIPPING_MUTED,
  UPDATE_VIDEO_METRICS,
} from 'services/MessagingService/EventNames';
import SessionStorageService from 'services/SessionStorageService';
import StreamingService from 'services/StreamingService';
import { HOUR_AS_SECONDS, MINUTE_AS_SECONDS } from 'enum/timeFormat';
import { DEFAULT_VOLUME_VALUE, STREAM_VOLUME_KEY } from 'enum/videoPlayer';


const KILOBYTE_AS_BYTES = 1000;

class PlayerService {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(PRIVATE_SHOW_STOP_SPY, this.handleSpyShowFullscreen);
    this.messagingService.on(UPDATE_VIDEO_METRICS, this.setVideoMetrics);
  }

  destructor() {
    this.messagingService.off(UPDATE_VIDEO_METRICS, this.setVideoMetrics);
    this.messagingService.off(PRIVATE_SHOW_STOP_SPY, this.handleSpyShowFullscreen);
  }

  // Video element
  @observable video = null;
  // typeof null === 'object'
  @observable volume = this.setDefaultVolume();

  // Current seek time
  @observable currentTime = 0;
  // Whole video duration
  @observable duration = 0;
  // Text that appears over the video
  @observable message = '';
  // Text that appears over the video
  @observable isLoading = true;
  @observable isPlaying = false;
  // flag for including controls for video
  @observable showControls = true;
  @observable isVideoMuted = SessionStorageService.get('profileVideoMuted') === 'true';
  @observable isBroadcastAudioMuted = SessionStorageService.get('broadcastAudioMuted') === 'true';
  // make controls visible
  @observable displayControls = false;
  // hls available stream quality options
  @observable streamQualityOptions = [];
  // first-time play muted video because of autoplay policy
  @observable showUnmuteOverlay = true;
  // tells if custom fullscreen is active or not
  @observable customFullscreenActive = false;
  @observable videoMetrics = {};

  @computed get seekValue() {
    return this.currentTime * (100 / this.duration);
  }

  setDefaultVolume = () => {
    const storedVolume = LocalStorageService.get(STREAM_VOLUME_KEY);

    if (typeof storedVolume === 'object') {
      return DEFAULT_VOLUME_VALUE;
    }

    return parseInt(storedVolume, 10);
  };

  // TODO
  @computed get showOverlay() {
    return !this.isPlaying;
  }

  @action setVideoMetrics = (metrics) => {
    if (metrics.viewerBR) {
      const { minBitrate, maxBitrate } = this.videoMetrics;
      const bitrates = {
        minBitrate: minBitrate ? Math.min(metrics.viewerBR, minBitrate) : metrics.viewerBR,
        maxBitrate: maxBitrate ? Math.max(metrics.viewerBR, maxBitrate) : metrics.viewerBR,
      };
      this.videoMetrics = { ...this.videoMetrics, ...metrics, ...bitrates };
    } else {
      this.videoMetrics = { ...this.videoMetrics, ...metrics };
    }
  };

  /**
   * Toggles the visibility of the controls
   * @param  {Boolean} flag - True for showing displays, false for hiding
   */
  @action toggleControls = (flag) => {
    this.displayControls = flag;
  };

  // Attach video to Player service
  @action setVideo(video) {
    // Check if valid video input
    this.isLoading = true;
    if (video) {
      this.video = video;
      // Attach listeners and handlers
      this.video.ontimeupdate = this.handleTimeUpdate;
    } else {
      this.message = 'Unable to play video';
      logger.error(this.message);
    }
  }

  // Update time handler
  @action handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target;
    this.currentTime = currentTime;
    this.duration = duration;
  };

  // Returns the remaining time and current time of the video  in this format 'mm:ss'  => 00:03
  @computed get timeDurationString() {
    return `${this.toHHMMSS(this.currentTime)} / ${this.toHHMMSS(this.duration)}`;
  }

  toHHMMSS = (val) => {
    const secNum = parseInt(val, 10);
    let hours = Math.floor(secNum / HOUR_AS_SECONDS);
    let minutes = Math.floor((secNum - (hours * HOUR_AS_SECONDS)) / MINUTE_AS_SECONDS);
    let seconds = secNum - (hours * HOUR_AS_SECONDS) - (minutes * MINUTE_AS_SECONDS);

    if (hours < 10) {
      hours = `0${hours}`;
    }
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    if (seconds < 10) {
      seconds = `0${seconds}`;
    }

    return `${hours}:${minutes}:${seconds}`;
  };

  /**
   * Shows a message on the overlay
   * @param  {String} message  Message that appears to the user
   */
  @action setMessage = (message) => {
    this.message = message;
  };

  @action setStreamQualityOptions = (values, isMobile, isJink = false) => {
    const options = values.map((value, ind) => {
      const dimmensionToShow = isMobile ? value.width : value.height;

      return {
        label: dimmensionToShow ? `${dimmensionToShow}p` : `${Math.round(value.bitrate / KILOBYTE_AS_BYTES)}kb/s`,
        key: ind,
        active: false,
      };
    });
    if (isJink) {
      options.push({
        label: translate('strings.resolution.auto'),
        key: -1,
        active: true,
      });
      this.streamQualityOptions = options;
    } else {
      options.unshift({
        label: translate('strings.resolution.auto'),
        key: -1,
        active: true,
      });
      this.streamQualityOptions = options.reverse();
    }
  };

  @computed get activeStreamQualityOption() {
    return this.streamQualityOptions.find((option) => option.active);
  }

  @action changeStreamQualityOption = (key) => {
    this.streamQualityOptions = this.streamQualityOptions.map((option) => {
      return {
        ...option,
        active: key === option.key,
      };
    });
  };

  @computed get showQualityOptions() {
    return this.streamQualityOptions.length > 1;
  }

  // Play video
  @action play = (destroyOnFinish = false) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'playerService::play::requested', withAds: destroyOnFinish });
    // eslint-disable-next-line no-console
    console.log('Trying to play video with sound...');
    this.isLoading = true;
    this.video.volume = this.volume / 100;
    const playPromise = this.video.play();
    if (playPromise) {
      return playPromise
        .then(() => {
          this.messagingService.emit(HLS_DEBUG, { method: 'playerService::play::success', withAds: destroyOnFinish });
          // eslint-disable-next-line no-console
          console.log('Success! Video is playing with sound now');
          this.playSuccess(false, destroyOnFinish);
        })
        .catch(() => {
          this.messagingService.emit(HLS_DEBUG, { method: 'playerService::play::error', withAds: destroyOnFinish });
          this.playMuted(destroyOnFinish);
        });
    }

    return undefined;

  };

  @action playMuted = (destroyOnFinish = false) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'playerService::playMuted::requested', withAds: destroyOnFinish });
    this.video.muted = true;
    // eslint-disable-next-line no-console
    console.log('Denied! Trying to play video muted...');

    return this.video
      .play()
      .then(() => {
        this.messagingService.emit(HLS_DEBUG, {
          method: 'playerService::playMuted::success',
          withAds: destroyOnFinish,
        });
        this.playSuccess(true, destroyOnFinish);
        // eslint-disable-next-line no-console
        console.log('Success! Video is playing muted now');
      })
      .catch((err) => {
        this.messagingService.emit(HLS_DEBUG, { method: 'playerService::playMuted::error', withAds: destroyOnFinish });
        this.isPlaying = false;
        this.isLoading = false;
        this.showUnmuteOverlay = false;
        this.handleError(err, false);
      });
  };

  @action playSuccess = (muted, destroyOnFinish = false) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'playerService::playSuccess', muted, withAds: destroyOnFinish });
    this.showUnmuteOverlay = muted;
    this.isVideoMuted = muted;
    this.isPlaying = true;
    this.isLoading = false;
    SessionStorageService.set('profileVideoMuted', muted);
    if (destroyOnFinish) {
      // TODO sometimes on FE side we have a bug when the viewer not able to see the stream
      //  and only able to see the background of the player
      //  problem related to ANY problem with ads/empty stream on FE (script loading fails, adblock, etc)
      //  HSL_DETACH will remove the stream and only ads service wil start stream again
      //  so, any problems with ads service will remove stream at all without any options to start again
      //  we should be definitely sure that ads service works before this event
      //  please add additional validation of VideoAdService.scriptLoaded here or something like this
      //  before HLS_DETACH event
      this.messagingService.emit(HLS_DETACH);
    } else {
      this.messagingService.emit(STREAM_STARTED, { muted });
    }
  };

  @action toggleMuted = () => {
    if (!this.isVideoMuted) {
      this.setVolume(0);
      this.isVideoMuted = true;
      SessionStorageService.set('profileVideoMuted', true);
    } else {
      this.setVolume(DEFAULT_VOLUME_VALUE);
      this.isVideoMuted = false;
      SessionStorageService.set('profileVideoMuted', false);
    }
  };

  @action toggleBroadcasterAudio = () => {
    const currentMutedValue = this.isBroadcastAudioMuted;
    this.isBroadcastAudioMuted = !currentMutedValue;
    StreamingService.toggleBroadcasterAudio();
    SessionStorageService.set('broadcastAudioMuted', !currentMutedValue);
  };

  @action unmuteVideoOnce = () => {
    this.showUnmuteOverlay = false;
    this.setVolume(this.volume || DEFAULT_VOLUME_VALUE);
    SessionStorageService.set('profileVideoMuted', false);
    this.messagingService.emit(STREAM_STARTED_TIPPING_MUTED);
  };

  // Pause video
  @action pause = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'playerService::pause' });
    this.video.pause();
    this.isPlaying = false;
  };

  @action setVolume = (val) => {
    const value = val / 100;
    this.volume = val;
    LocalStorageService.set(STREAM_VOLUME_KEY, JSON.stringify(val));
    this.video.muted = !val;
    this.isVideoMuted = !val;
    SessionStorageService.set('profileVideoMuted', !val);
    this.showUnmuteOverlay = false;
    this.video.volume = value;
  };

  // Cross browser full screen
  @action enterFullscreen = (element) => {
    const el = element || this.video;
    let result = undefined;
    if (el.requestFullscreen) {
      result = el.requestFullscreen();
    } else if (el.mozRequestFullScreen) {
      result = el.mozRequestFullScreen();
    } else if (el.webkitRequestFullscreen) {
      result = el.webkitRequestFullscreen();
    }

    return result;
  };

  @action isInFullscreen = () =>
    Boolean(
      (document.fullscreenElement && document.fullscreenElement !== null) ||
      (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) ||
      (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
      (document.msFullscreenElement && document.msFullscreenElement !== null)
    );

  @action exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  /**
   *  Handle picture in picture event
   */
  pip = () => {
    if (this.video.requestPictureInPicture) {
      this.video.requestPictureInPicture().catch(() => {
        this.handleError('Unable to process picture in picture', false);
      });
    }
  };

  /**
   *  Close fullscreen if user finishes spy show
   */
  handleSpyShowFullscreen = () => {
    if (document.fullscreen) {
      this.exitFullscreen();
    }
  };

  /**
   *  Exit picture in picture
   */
  exitPip = () => {
    this.video
      .exitPictureInPicture()
      .then(() => {
        this.play();
      })
      .catch(() => {
        logger.error('Unable to exit picture in picture');
      });
  };

  /**
   * Seek bar interpetation - changes current time based on the slider position
   *
   * @param  {any} e  Event
   * @param  {Number} value  Value of the slider ranging from 0 to 100
   */
  @action seek = (e, value) => {
    const seekto = this.duration * (value / 100);
    this.video.currentTime = seekto;
  };

  /**
   *  Error handler
   *
   * @param  {String} error  Error message
   * @param  {Boolean} stopPlayer Flag wether it's a breaking change, to stop the player
   */
  @action handleError = (error, stopPlayer) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'playerService::handleError', error, stopPlayer });
    this.message = error;
    logger.error(error);
    if (stopPlayer) {
      this.isLoading = false;
    }
  };
}

// Export default as instance
export default new PlayerService();
// Export  as class
export { PlayerService };
